import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { SessionExpiryProvider } from "./components/SessionExpiryProvider";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import Services from "./pages/Services";
import About from "./pages/About";
import Contact from "./pages/Contact";
import LiveScan from "./pages/LiveScan";
import LiveScanPdf from "./pages/LiveScanPdf";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Unsubscribe from "./pages/Unsubscribe";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Subprocessors from "./pages/Subprocessors";
import Affiliates from "./pages/Affiliates";
import Security from "./pages/Security";

import WebsiteCreations from "./pages/WebsiteCreations";
import NotFound from "./pages/NotFound";
import LinkedInLanding from "./pages/LinkedInLanding";
import TrialOnboardingHelp from "./pages/TrialOnboardingHelp";

// Portal pages
import PortalAuth from "./pages/portal/Auth";
import PortalDashboard from "./pages/portal/Dashboard";
import AdminPanel from "./pages/portal/AdminPanel";
import AdminImpersonationDashboard from "./pages/portal/AdminImpersonationDashboard";
import AddDomain from "./pages/portal/AddDomain";
import PortalSuccess from "./pages/portal/Success";
import PortalAccount from "./pages/portal/Account";
import PortalBilling from "./pages/portal/Billing";
import SecurityAudit from "./pages/portal/SecurityAudit";
import ResetPassword from "./pages/portal/ResetPassword";
import InviteOnboarding from "./pages/portal/InviteOnboarding";
import CompletePurchase from "./pages/portal/CompletePurchase";
import ConfirmDomainRemoval from "./pages/portal/ConfirmDomainRemoval";
import AutoScanRoute from "./pages/portal/AutoScanRoute";
import Promotions from "./pages/portal/Promotions";
import DemoDashboard from "./pages/portal/Demo";
import DemoDomainLayout from "./pages/portal/DemoDomainLayout";
import DemoDomainOverview from "./pages/portal/DemoDomainOverview";
import DemoDomainCoverage from "./pages/portal/DemoDomainCoverage";
import DemoDomainLogs from "./pages/portal/DemoDomainLogs";
import DemoDomainEvidence from "./pages/portal/DemoDomainEvidence";
import DomainLayout from "./pages/portal/DomainLayout";
import DomainOverview from "./pages/portal/DomainOverview";
import DomainCoverage from "./pages/portal/DomainCoverage";
import DomainLogs from "./pages/portal/DomainLogs";
import DomainEvidence from "./pages/portal/DomainEvidence";
import DomainSettings from "./pages/portal/DomainSettings";
import DomainAccessPolicy from "./pages/portal/DomainAccessPolicy";
import ControlDriftReport from "./pages/portal/ControlDriftReport";
import { RequireAuth } from "./components/portal/RequireAuth";
import { Helmet } from "@/components/Helmet";
import CookieBanner from "@/components/CookieBanner";
import { useAnalyticsConsent } from "@/hooks/useAnalyticsConsent";
import { DemoModeProvider } from "@/demo/DemoContext";

const queryClient = new QueryClient();

const SITE_ORIGIN = "https://shadowscan.com.au";
const CANONICAL_ROUTE_MAP: Record<string, string> = {
  "/lp/wordpress-security-trial": "/linkedin",
  "/help/trial-onboarding": "/help/add-domains",
};

const CanonicalMeta = () => {
  const location = useLocation();
  const canonicalPathname = CANONICAL_ROUTE_MAP[location.pathname] ?? location.pathname;
  const normalizedPath =
    canonicalPathname === "/"
      ? ""
      : canonicalPathname.replace(/\/$/, "");
  const href = `${SITE_ORIGIN}${normalizedPath}`;

  return (
    <Helmet>
      <link rel="canonical" href={href} />
    </Helmet>
  );
};

const App = () => (
  <MatomoLoader>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ImpersonationProvider>
            <SessionExpiryProvider>
              <DemoModeProvider>
                <Toaster />
                <Sonner />
                <PointerEventsReset />
                <BrowserRouter>
                  <ScrollToTop />
                  <CanonicalMeta />
                  <Routes>
                  <Route path="/" element={<Index />} />
                <Route path="/services" element={<Services />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/live-scan/:scanId/:domain.pdf" element={<LiveScanPdf />} />
                <Route path="/live-scan/:scanId" element={<LiveScan />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/security" element={<Security />} />
                <Route path="/subprocessors" element={<Subprocessors />} />
                <Route path="/affiliates" element={<Affiliates />} />
                <Route path="/enterprise" element={<Navigate to="/" replace />} />
                <Route path="/websites" element={<WebsiteCreations />} />
                <Route path="/linkedin" element={<LinkedInLanding />} />
                <Route path="/lp/wordpress-security-trial" element={<LinkedInLanding />} />
                <Route path="/help/add-domains" element={<TrialOnboardingHelp />} />
                <Route path="/help/trial-onboarding" element={<TrialOnboardingHelp />} />
                
                {/* Portal routes */}
                <Route path="/portal/auth" element={<Navigate to="/portal/login" replace />} />
                <Route path="/portal/login" element={<PortalAuth />} />
                <Route path="/portal/sign-up" element={<PortalAuth />} />
                <Route path="/portal" element={<PortalDashboard />} />
                <Route path="/portal/domains" element={<PortalDashboard />} />
                <Route
                  path="/portal/domains/:domainId"
                  element={<RequireAuth><DomainLayout /></RequireAuth>}
                >
                  <Route index element={<DomainOverview />} />
                  <Route path="owasp" element={<Navigate to="../coverage" replace />} />
                  <Route path="coverage" element={<DomainCoverage />} />
                  <Route path="access-policy" element={<DomainAccessPolicy />} />
                  <Route path="logs" element={<DomainLogs />} />
                  <Route path="evidence" element={<DomainEvidence />} />
                  <Route path="settings" element={<DomainSettings />} />
                </Route>
                <Route path="/portal/add-domain" element={<AddDomain />} />
                <Route path="/portal/success" element={<PortalSuccess />} />
                <Route path="/portal/account" element={<PortalAccount />} />
                <Route path="/portal/billing" element={<PortalBilling />} />
                <Route path="/portal/security-audit" element={<SecurityAudit />} />
                <Route path="/portal/invite-onboarding" element={<InviteOnboarding />} />
                <Route path="/portal/complete-purchase" element={<RequireAuth><CompletePurchase /></RequireAuth>} />
                <Route path="/portal/confirm-domain-removal" element={<RequireAuth><ConfirmDomainRemoval /></RequireAuth>} />
                <Route path="/portal/admin" element={<RequireAuth><AdminPanel /></RequireAuth>} />
                <Route path="/portal/admin/users" element={<RequireAuth><AdminPanel /></RequireAuth>} />
                <Route path="/portal/admin/sites" element={<RequireAuth><AdminPanel /></RequireAuth>} />
                <Route path="/portal/admin/tools" element={<RequireAuth><AdminPanel /></RequireAuth>} />
                <Route path="/portal/admin/control-drift" element={<RequireAuth><ControlDriftReport /></RequireAuth>} />
                <Route path="/portal/admin/impersonate/:userId" element={<RequireAuth><AdminImpersonationDashboard /></RequireAuth>} />
                <Route path="/portal/autoscan" element={<AutoScanRoute />} />
                <Route path="/portal/promotions" element={<Promotions />} />
                <Route path="/portal/reset-password" element={<ResetPassword />} />
                <Route path="/portal/demo" element={<DemoDashboard />} />
                <Route path="/portal/demo/domains/:siteId" element={<DemoDomainLayout />}>
                  <Route index element={<DemoDomainOverview />} />
                  <Route path="owasp" element={<Navigate to="../coverage" replace />} />
                  <Route path="coverage" element={<DemoDomainCoverage />} />
                  <Route path="logs" element={<DemoDomainLogs />} />
                  <Route path="evidence" element={<DemoDomainEvidence />} />
                </Route>
                <Route path="/demo" element={<Navigate to="/portal/demo" replace />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                  <CookieBanner />
                </BrowserRouter>
              </DemoModeProvider>
            </SessionExpiryProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </MatomoLoader>
);

const PointerEventsReset = () => {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const resetIfStuck = () => {
      if (document.body.style.pointerEvents !== "none") return;
      const openDialog = document.querySelector('[data-state="open"][role="dialog"]');
      if (!openDialog) {
        document.documentElement.style.pointerEvents = "";
        document.body.style.pointerEvents = "";
      }
    };
    const observer = new MutationObserver(resetIfStuck);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    resetIfStuck();
    return () => observer.disconnect();
  }, []);
  return null;
};

const MatomoLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const consent = useAnalyticsConsent();

  useEffect(() => {
    if (consent !== "accepted") {
      return;
    }

    const existing = document.getElementById("matomo-script");
    if (existing) return;

    // Analytics events only fire after consent (Privacy Policy: analytics data handling).
    // Initialize Matomo tracking array
    const _paq = (window as { _paq?: unknown[][] })._paq = (window as { _paq?: unknown[][] })._paq || [];
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);

    const MATOMO_URL = "https://analytics.shadowscan.cloud/";
    _paq.push(['setTrackerUrl', MATOMO_URL + 'matomo.php']);
    _paq.push(['setSiteId', '1']);

    // Load Matomo script using safe DOM API instead of innerHTML
    const script = document.createElement('script');
    script.async = true;
    script.src = MATOMO_URL + 'matomo.js';
    script.id = 'matomo-script';
    document.head.appendChild(script);
  }, [consent]);

  return <>{children}</>;
};

export default App;
